import { useCallback, useContext, useMemo } from 'react';
import { Campaign, getCampaignConfig } from 'content/campaigns';
import { CartApiService } from '../services/CartApiService';
import {
  Cart,
  CartLineAttribute,
  CartLineInput,
  CartRequest,
  PolicyCartInput,
} from 'lib/commerce/cart/types';
import { runClientPolicies } from 'lib/commerce/cart/policies';
import { cartContainsRequestedMerchandise } from 'lib/commerce/cart/cartContainsRequestedMerchandise';
import {
  CartFlow,
  CartDiscountOptions,
  CartPolicyMeta,
} from 'lib/commerce/cart/policies/types';
import {
  getMaxQuantityReachedMessage,
  parseLineMaxQuantity,
} from 'lib/commerce/cart/policies/cartLineHelpers';
import { ToastContext } from '../../Toast';
import { addToCartArgsType, Line, ProductType } from '../types';
import { applyRingAirGiftStamp, cartHasGiftFlow } from '../giftStamp';
import {
  classifyCartError,
  getCartApiErrorMessage,
  isCartMutateSuccess,
  withTimeout,
  type CartMutateResponse,
} from '../utils';
import {
  computeHasTradeIn,
  computeProductTypeFlags,
  normalizeAddToCartArgs,
} from './normalizeAddToCartArgs';
import { CartOperations, CartState } from './types';

const REREAD_TIMEOUT_MS = 5000;

const toAttrs = (
  attributes?: Array<Record<string, string>>,
): CartLineAttribute[] | undefined =>
  attributes as unknown as CartLineAttribute[] | undefined;

export const useCartOperations = (
  cartState: CartState,
  discountCodes: Array<string>,
  setDiscountCodes: (discounts: Array<string>, options: any) => Array<string>,
  showCart: () => void,
  ultrahumanXAdded: any,
  checkAddonsAddedToCart: () => void,
  setActiveFlowScreen: (screen: any) => void,
  getCampaignForProduct: (
    product: 'ring' | 'ring-pro' | 'home' | 'cgm',
  ) => Campaign | undefined,
  region: string | null,
): CartOperations => {
  const { cart, store, setCart, deleteCartFromLocalStorage, urlParams } =
    cartState;
  const { showToast } = useContext(ToastContext);

  const api = useMemo(() => (store ? new CartApiService(store) : null), [store]);

  const buildMeta = useCallback(
    (
      flow: CartFlow,
      extra: {
        discountOptions?: CartDiscountOptions;
        isUpsell?: boolean;
        createNewCart?: boolean;
        removedLineId?: string;
      } = {},
    ): CartPolicyMeta => {
      const cgmConfig = getCampaignConfig(getCampaignForProduct('cgm'));
      return {
        flow,
        store,
        discountOptions: extra.discountOptions ?? {},
        ultrahumanXAdded: ultrahumanXAdded ? { id: ultrahumanXAdded.id } : null,
        isUpsell: extra.isUpsell ?? false,
        createNewCart: extra.createNewCart ?? false,
        isCgmCampaignActive: Boolean(cgmConfig?.active),
        cgmCampaignDiscountCode: cgmConfig?.discountCode,
        pagePath: typeof window !== 'undefined' ? window.location.pathname : '',
        removedLineId: extra.removedLineId,
      };
    },
    [store, ultrahumanXAdded, getCampaignForProduct],
  );

  const rereadCart = useCallback(
    async (cartId: string): Promise<CartMutateResponse | null> => {
      if (!api) return null;
      for (let attempt = 0; attempt <= 1; attempt++) {
        const result = await withTimeout(api.mutateCart({ cartId }), REREAD_TIMEOUT_MS);
        if (result && isCartMutateSuccess(result)) return result;
      }
      return null;
    },
    [api],
  );

  const finalizeMutation = useCallback(
    async (
      response: CartMutateResponse,
      cartId: string | undefined,
      errorCopy: string,
    ): Promise<{ success: boolean; cart?: Cart | null }> => {
      const errorClass = classifyCartError(response);

      if (errorClass === 'discount') {
        let finalCart = isCartMutateSuccess(response) ? response.cart : null;
        const rereadId = cartId ?? finalCart?.id;
        if (rereadId) {
          const reread = await rereadCart(rereadId);
          if (reread && isCartMutateSuccess(reread) && reread.cart) {
            finalCart = reread.cart;
          }
        }
        if (finalCart) {
          setCart(finalCart);
          return { success: true, cart: finalCart };
        }
      }

      if (
        errorClass === 'transport' ||
        errorClass === 'state' ||
        errorClass === 'validation'
      ) {
        if (errorClass !== 'validation') {
          const rereadId =
            cartId ?? (isCartMutateSuccess(response) ? response.cart?.id : undefined);
          if (rereadId) {
            const reread = await rereadCart(rereadId);
            if (reread && isCartMutateSuccess(reread)) {
              if (!reread.cart || reread.cart.totalQuantity === 0) {
                deleteCartFromLocalStorage();
              } else {
                setCart(reread.cart);
              }
            }
          }
        }
        showToast(getCartApiErrorMessage(response, errorCopy));
        return { success: false };
      }

      if (isCartMutateSuccess(response) && response.cart) {
        setCart(response.cart);
        return { success: true, cart: response.cart };
      }

      showToast(getCartApiErrorMessage(response, errorCopy));
      deleteCartFromLocalStorage();
      return { success: false };
    },
    [rereadCart, setCart, deleteCartFromLocalStorage, showToast],
  );

  const executeCartMutation = useCallback(
    async (
      policyInput: PolicyCartInput,
      meta: CartPolicyMeta,
      opts: {
        errorCopy: string;
        createOnly?: { cartAttributes?: CartLineAttribute[]; countryCode?: string };
      },
    ): Promise<{ success: boolean; cart?: Cart | null; blocked?: boolean }> => {
      if (!api) return { success: false };

      const result = runClientPolicies(policyInput, (cart as Cart | null) ?? null, meta);
      if (result.blocked) {
        showToast(result.userMessage ?? 'Unable to update cart.');
        return { success: false, blocked: true };
      }
      if (result.userMessage) showToast(result.userMessage);

      const cartId = meta.createNewCart ? undefined : cart?.id;
      const request: CartRequest = { ...result.input, cartId };

      if (result.input.discountCodes !== undefined) {
        request.discountCodes = setDiscountCodes(
          result.input.discountCodes,
          meta.discountOptions,
        );
      }

      if (!cartId && opts.createOnly) {
        if (opts.createOnly.cartAttributes !== undefined) {
          request.cartAttributes = opts.createOnly.cartAttributes;
        }
        if (opts.createOnly.countryCode) {
          request.countryCode = opts.createOnly.countryCode;
        }
      }

      const response = await api.mutateCart(request);
      const finalized = await finalizeMutation(response, cartId, opts.errorCopy);
      if (
        finalized.success &&
        request.lineItemsToAdd?.length &&
        !cartContainsRequestedMerchandise(finalized.cart, request.lineItemsToAdd)
      ) {
        showToast(getCartApiErrorMessage(response, opts.errorCopy));
        return { success: false };
      }
      return finalized;
    },
    [api, cart, setDiscountCodes, showToast, finalizeMutation],
  );

  /** A gift cart must carry `_gift` on its Ring AIR lines before checkout —
   *  that attribute is what makes the order ineligible for SDD/NDD. */
  const stampRingAirGift = useCallback(
    async (nextCart: Cart) => {
      if (!store || !cartHasGiftFlow(nextCart)) return;
      const stamped = await applyRingAirGiftStamp({
        cart: nextCart,
        store,
        giftOn: true,
      });
      if (stamped !== nextCart) setCart(stamped);
    },
    [store, setCart],
  );

  const createCartWithItem = useCallback(
    async (
      lines: Line,
      shouldShowCart = true,
      customDiscountCodes?: Array<string>,
    ): Promise<boolean> => {
      if (!api) return false;
      const response = await api.mutateCart({
        lineItemsToAdd: lines as unknown as CartLineInput[],
        cartAttributes: (urlParams ?? []) as unknown as CartLineAttribute[],
        discountCodes: customDiscountCodes ?? discountCodes,
        countryCode: region && region !== 'ROW' ? region : undefined,
      });
      if (!isCartMutateSuccess(response) || !response.cart) {
        showToast('Failed to add item to cart. Please try again.');
        return false;
      }
      setCart(response.cart);
      await stampRingAirGift(response.cart);
      checkAddonsAddedToCart();
      if (shouldShowCart) showCart();
      return true;
    },
    [
      api,
      urlParams,
      discountCodes,
      region,
      setCart,
      stampRingAirGift,
      checkAddonsAddedToCart,
      showCart,
      showToast,
    ],
  );

  const addToCartInternal = useCallback(
    async (
      argsArray: ReadonlyArray<addToCartArgsType>,
      createNewCart = false,
      shouldShowCart = true,
      isUpsell = false,
    ): Promise<boolean> => {
      if (!store || !api) return false;

      const normalized = normalizeAddToCartArgs(argsArray, discountCodes);
      if (!normalized.lineItemsToAdd.length) return false;

      const existingCartHasM1 = Boolean(
        cart?.lines?.edges?.some((edge: any) =>
          edge.node.attributes?.some(
            (a: any) => a.key === '_product_type' && a.value === ProductType.M1,
          ),
        ),
      );
      const flags = computeProductTypeFlags(argsArray);
      const discountOptions: CartDiscountOptions = {
        ...flags,
        isM1ProductTypePresent: flags.isM1ProductTypePresent || existingCartHasM1,
        isUhxProductTypePresent:
          flags.isUhxProductTypePresent || Boolean(ultrahumanXAdded),
        hasTradeIn: computeHasTradeIn(argsArray),
      };

      const isCreate = createNewCart || !cart?.id;
      const meta = buildMeta('add', { discountOptions, isUpsell, createNewCart });
      const result = await executeCartMutation(
        {
          lineItemsToAdd: normalized.lineItemsToAdd,
          discountCodes: normalized.discountCodes,
        },
        meta,
        {
          errorCopy: 'Failed to add item to cart. Please try again.',
          createOnly: isCreate
            ? {
                cartAttributes: (urlParams ?? []) as unknown as CartLineAttribute[],
                countryCode: region && region !== 'ROW' ? region : undefined,
              }
            : undefined,
        },
      );

      if (!result.success || !result.cart) return false;

      await stampRingAirGift(result.cart);
      setActiveFlowScreen(0);
      if (shouldShowCart) showCart();
      checkAddonsAddedToCart();
      return true;
    },
    [
      store,
      api,
      cart,
      discountCodes,
      region,
      urlParams,
      buildMeta,
      executeCartMutation,
      stampRingAirGift,
      setActiveFlowScreen,
      showCart,
      checkAddonsAddedToCart,
    ],
  );

  const deleteFromCart = useCallback(
    async (clid: string): Promise<boolean | undefined> => {
      if (!store || !clid || !api || !cart) return;

      const meta = buildMeta('remove', { removedLineId: clid });
      const result = await executeCartMutation(
        { lineItemsToRemove: [clid], discountCodes: [...discountCodes] },
        meta,
        { errorCopy: 'Unable to remove item. Please try again.' },
      );
      if (!result.success) return false;
      return true;
    },
    [store, api, cart, discountCodes, buildMeta, executeCartMutation],
  );

  const updateCartQuantity = useCallback(
    async (
      clid: string,
      quantity: number,
      attributes?: Array<Record<string, string>>,
    ): Promise<boolean | undefined> => {
      if (!store || !clid || !api || !cart?.id) return;

      const cartLine = cart.lines?.edges?.find((e: any) => e.node.id === clid);
      const lineMax = parseLineMaxQuantity(cartLine?.node?.attributes);
      let nextQuantity = quantity;
      if (lineMax && quantity > lineMax) {
        showToast(getMaxQuantityReachedMessage(lineMax));
        if (cartLine?.node?.quantity >= lineMax) return true;
        nextQuantity = lineMax;
      }

      if (quantity === 0) return deleteFromCart(clid);

      const result = await executeCartMutation(
        {
          lineItemsToModify: [
            { id: clid, quantity: nextQuantity, attributes: toAttrs(attributes) },
          ],
        },
        buildMeta('update'),
        { errorCopy: 'Unable to update quantity. Please try again.' },
      );
      if (!result.success) return false;

      // Skip opening the cart drawer for products with inline quantity
      // controls on their own pages (performance-lab buy page, supplements on
      // the Luminary LP) — inside the drawer this is a no-op anyway since it's
      // already visible.
      const productType = cartLine?.node?.attributes?.find(
        (a: any) => a.key === '_product_type',
      )?.value;
      if (
        productType !== ProductType.PERFORMANCE_LAB &&
        productType !== ProductType.SUPPLEMENT
      ) {
        showCart();
      }
      return true;
    },
    [store, api, cart, buildMeta, executeCartMutation, deleteFromCart, showCart, showToast],
  );

  const incrementCartQuantity = useCallback(
    async (cartLine: any): Promise<boolean | undefined> => {
      if (!store || !cartLine || !api) return;

      const lineMax = parseLineMaxQuantity(cartLine.node?.attributes);
      if (lineMax && cartLine.node?.quantity >= lineMax) {
        showToast(getMaxQuantityReachedMessage(lineMax));
        return true;
      }

      const siblingAttr = cartLine.node.attributes?.find(
        (a: any) => a.key === 'sibling_id',
      );
      if (!siblingAttr) {
        return updateCartQuantity(cartLine.node.id, cartLine.node.quantity + 1);
      }
      if (!cart?.id) return;

      const response = await api.mutateCart({
        cartId: cart.id,
        lineItemsToAdd: [
          {
            merchandiseId: cartLine.node.merchandise.id,
            quantity: 1,
            attributes: [siblingAttr],
          },
        ],
        discountCodes: [...discountCodes],
      });
      const result = await finalizeMutation(
        response,
        cart.id,
        'Unable to update quantity. Please try again.',
      );
      if (!result.success || !result.cart) return false;

      await stampRingAirGift(result.cart);
      setActiveFlowScreen(0);
      showCart();
      checkAddonsAddedToCart();
      return true;
    },
    [
      store,
      api,
      cart,
      discountCodes,
      updateCartQuantity,
      finalizeMutation,
      setActiveFlowScreen,
      showCart,
      checkAddonsAddedToCart,
      showToast,
    ],
  );

  const removeFromCart = useCallback(
    async (
      clid: string,
      currentQuantity: number,
      attributes?: Array<Record<string, string>>,
      discountCode?: string,
    ): Promise<boolean | undefined> => {
      if (!store || !clid || !currentQuantity || !api) return;

      const discountArray = [...discountCodes];
      if (discountCode) {
        const idx = discountArray.indexOf(discountCode);
        if (idx !== -1) discountArray.splice(idx, 1);
      }
      setDiscountCodes(discountArray, {});

      if (currentQuantity === 1) return deleteFromCart(clid);

      const result = await executeCartMutation(
        {
          lineItemsToModify: [
            { id: clid, quantity: currentQuantity - 1, attributes: toAttrs(attributes) },
          ],
        },
        buildMeta('update'),
        { errorCopy: 'Unable to remove item. Please try again.' },
      );
      if (!result.success) return false;
      showCart();
      return true;
    },
    [
      store,
      api,
      discountCodes,
      setDiscountCodes,
      buildMeta,
      executeCartMutation,
      deleteFromCart,
      showCart,
    ],
  );

  return {
    addToCartInternal,
    removeFromCart,
    deleteFromCart,
    updateCartQuantity,
    incrementCartQuantity,
    createCartWithItem,
  };
};
